import { observer } from 'mobx-react';
import React from 'react';
import { Input } from 'reactstrap';
import { PrePaymentFrequencyOption } from '~/lib/ApiModel';
import { getAppContext } from '~/lib/AppContext';
import { hasItem } from '~/lib/helper/ArrayHelper';
import { nearestCent } from '~/lib/helper/CurrencyHelper';
import { formatCurrency } from '~/lib/helper/StringHelper';
import { Def, DefTable, DefTableProps } from './DefTable';

import './BillingDetails.scss';

type BillingDetailsProps = {
    previousSubtotal?: number;
    subtotal: number;
    prePaidPeriodRemainingFraction?: number;
    prePaymentFrequency?: PrePaymentFrequencyOption;
    prePaymentFrequencyOptions?: PrePaymentFrequencyOption[];
    onPrePaymentFrequencyChange?: (freq: PrePaymentFrequencyOption) => void;
};

@observer
export default class BillingDetails extends React.Component<BillingDetailsProps & DefTableProps> {
    get taxCode() {
        return getAppContext().customer.item?.tax_code ?? { type: 'none', name: 'Tax' };
    }

    computeTax(subtotal: number) {
        if (this.taxCode.type !== 'scalar') return 0;
        const tax = nearestCent((subtotal * (this.taxCode.fixed_percent ?? 0)) / 100);
        return tax;
    }

    get tax(): number {
        return this.computeTax(this.props.subtotal);
    }

    get total(): number {
        return nearestCent(this.tax + this.props.subtotal);
    }

    get previousTax(): number {
        return this.computeTax(this.props.previousSubtotal ?? 0);
    }

    get previousTotal(): number {
        return nearestCent(this.previousTax + (this.props.previousSubtotal ?? 0));
    }

    get payableToday(): number {
        const { prePaymentFrequency, prePaidPeriodRemainingFraction = 1 } = this.props;

        if (!prePaymentFrequency || this.isPostPaid) return 0;

        // Amount due is calculated as a per month price difference * prepaid months * fraction of prepaid period remaining * (1 - discount percentage)
        const per_month_diff = this.total - this.previousTotal;

        const total_diff = per_month_diff * prePaymentFrequency.pre_payment_period_months;

        const pro_rated_diff = total_diff * prePaidPeriodRemainingFraction;

        const pro_rated_diff_with_prepaid_discount = nearestCent(
            pro_rated_diff * (1 - prePaymentFrequency.discount_percentage / 100)
        );

        return pro_rated_diff_with_prepaid_discount;
    }

    get isPostPaid(): boolean {
        return (this.props.prePaymentFrequency?.pre_payment_frequency ?? 'post-paid') === 'post-paid';
    }

    get isChangePlan(): boolean {
        return this.props.previousSubtotal !== undefined;
    }

    renderChange() {
        const prevSubtotal = this.props.previousSubtotal;
        if (typeof prevSubtotal !== 'number') return null;
        const change = nearestCent(this.total - nearestCent(prevSubtotal + this.computeTax(prevSubtotal)));
        return (
            <Def name="Monthly Change">
                {change < 0 ? '-' : '+'}
                {formatCurrency(Math.abs(change))}{' '}
            </Def>
        );
    }

    render() {
        const {
            previousSubtotal,
            subtotal,
            ref,
            prePaymentFrequency: paymentFrequency,
            prePaymentFrequencyOptions: paymentFrequencyOptions,
            onPrePaymentFrequencyChange: onPaymentFrequencyChange,
            prePaidPeriodRemainingFraction,
            ...defTableProps
        } = this.props;

        return (
            <div className="billingDetails">
                <DefTable {...defTableProps} className={(defTableProps.className ?? '') + ' wide mb-3'}>
                    {this.renderChange()}
                    <Def name="Monthly Total">
                        {formatCurrency(this.total)}{' '}
                        {this.tax > 0 && `(incl. ${formatCurrency(this.tax)} ${this.taxCode.name})`}
                    </Def>
                    {paymentFrequencyOptions && hasItem(paymentFrequencyOptions) && (
                        <Def name="Payment Frequency">
                            <Input
                                type="select"
                                aria-label="Payment frequency"
                                value={
                                    paymentFrequency?.pre_payment_frequency ??
                                    paymentFrequencyOptions[0].pre_payment_frequency
                                }
                                onChange={(e) =>
                                    onPaymentFrequencyChange?.(
                                        paymentFrequencyOptions.find(
                                            (pf) => pf.pre_payment_frequency === e.currentTarget.value
                                        ) ?? paymentFrequencyOptions[0]
                                    )
                                }
                            >
                                {paymentFrequencyOptions.map((pf) => (
                                    <option key={pf.pre_payment_frequency} value={pf.pre_payment_frequency}>
                                        {pf.description}
                                    </option>
                                ))}
                            </Input>
                        </Def>
                    )}
                    {!this.isPostPaid && (
                        <Def name={this.payableToday >= 0 ? 'Total Due' : 'Credit Provided'}>
                            {formatCurrency(this.payableToday < 0 ? this.payableToday * -1 : this.payableToday)}
                        </Def>
                    )}
                </DefTable>
            </div>
        );
    }
}
